import { CREDIT_TYPE_CARD, CREDIT_TYPE_TERM_LOAN, FundingPartnerSearchResult } from '../types';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import React, { useMemo } from 'react';
import { openUrlInNewTab } from '../../util/window-utils';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { Button } from 'react-bootstrap';
import { useCurrentUser } from '../../user/hooks/useCurrentUser';
import { composeExternalUrl } from '../../marketplace/utils';
import { OpenExternalIcon } from '../../assets/icons';
import { StyleVariables } from '../../commons/styleConstants';
import { NewTabLink } from '../../commons/components/NewTabLink';
import { shortAmount } from '../../commons/utils';
import { formatMoney } from '../../util/utils';
import { FundingPartnerMatchIcon } from './FundingPartnerMatchIcon';
import { fundingPartnerHasMismatches } from '../utils';
import clsx from 'clsx';

const FUNDING_PARTNER_LOGO_HEIGHT = '36px';

export const FundingPartnerTile = (props: {
  fundingPartnerSearchResult: FundingPartnerSearchResult;
  onIntroductionRequested: () => void;
}) => {
  const {
    fundingPartnerSearchResult: { fundingPartner, match },
    onIntroductionRequested,
  } = props;
  const user = useCurrentUser();

  const url = fundingPartner.url ? composeExternalUrl(fundingPartner.url, user) : null;
  const color =
    fundingPartner.creditType === CREDIT_TYPE_CARD
      ? StyleVariables.cyan500
      : StyleVariables.green500;
  const minMaxLoanAmount = useMemo(() => {
    const minAmount = shortAmount(fundingPartner.minLoanAmount);
    const maxAmount = shortAmount(fundingPartner.maxLoanAmount);

    return minAmount && maxAmount
      ? `${minAmount} - ${maxAmount}`
      : minAmount
      ? `from ${minAmount}`
      : maxAmount
      ? `up to ${maxAmount}`
      : '';
  }, [fundingPartner]);
  const reqs = fundingPartner.requirements;
  const hasMismatches = fundingPartnerHasMismatches(match);
  return (
    <div
      className={clsx('funding__partner-container', {
        'funding__partner-container--mismatch': hasMismatches,
      })}
    >
      <div>
        {fundingPartner.logo && (
          <NewTabLink
            href={url}
            analyticsEvent={AnalyticsEvent.FUNDING_LINK_OPENED}
            analyticsProperties={{ code: fundingPartner.code, source: 'logo' }}
          >
            <img
              src={fundingPartner.logo}
              alt={fundingPartner.name}
              height={FUNDING_PARTNER_LOGO_HEIGHT}
            />
          </NewTabLink>
        )}
        <div className="funding__partner-header-line">
          <span className="growegy-font16--weight700 growegy-font-em">{fundingPartner.name}</span>
          <NewTabLink
            href={url}
            analyticsEvent={AnalyticsEvent.FUNDING_LINK_OPENED}
            analyticsProperties={{ code: fundingPartner.code, source: 'domain' }}
          >
            <span className="growegy-title12--em">{fundingPartner.domain}</span>
            {url && <OpenExternalIcon className="funding__open-external-icon" />}
          </NewTabLink>
          <div className="funding__credit-type" style={{ borderColor: color, color }}>
            {fundingPartner.creditType === CREDIT_TYPE_CARD ? 'Business Credit Card' : 'Term Loan'}
          </div>
          {minMaxLoanAmount && (
            <div>
              {`${
                fundingPartner.creditType === CREDIT_TYPE_TERM_LOAN ? 'Loan' : 'Credit limit'
              }: ${minMaxLoanAmount} `}{' '}
              {match.loanAmount.isMatch != null && (
                <FundingPartnerMatchIcon
                  uniqueId={`${fundingPartner.code}-loanAmount`}
                  match={match.loanAmount}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div className="growegy-font14--weight500 growegy-font-em">{fundingPartner.pitch}</div>
      <div>
        <Markdown className="markdown-container" remarkPlugins={[remarkGfm]}>
          {fundingPartner.descriptionMarkdown}
        </Markdown>
      </div>
      {(reqs.minPersonalCreditScore ||
        reqs.minAnnualPersonalIncome ||
        reqs.maxDebtToIncomeRatio ||
        reqs.minMonthlyRevenue ||
        reqs.minMonthsInBusiness) && (
        <div>
          <div className="growegy-font14--weight500 growegy-font-em">Requirements:</div>
          {reqs.minPersonalCreditScore && (
            <div>
              <FundingPartnerMatchIcon
                uniqueId={`${fundingPartner.code}-minPersonalCreditScore`}
                match={match.minPersonalCreditScore}
              />{' '}
              Personal credit score: {reqs.minPersonalCreditScore}+
            </div>
          )}
          {reqs.minMonthsInBusiness && (
            <div>
              <FundingPartnerMatchIcon
                uniqueId={`${fundingPartner.code}-minMonthsInBusiness`}
                match={match.minMonthsInBusiness}
              />{' '}
              Min company age: {reqs.minMonthsInBusiness} months
            </div>
          )}
          {reqs.minAnnualPersonalIncome && (
            <div>
              <FundingPartnerMatchIcon
                uniqueId={`${fundingPartner.code}-minAnnualPersonalIncome`}
                match={match.minAnnualPersonalIncome}
              />{' '}
              Personal annual income: {shortAmount(reqs.minAnnualPersonalIncome)}+
              {reqs.annualPersonalIncomeWindowYears === 1
                ? ' for the last year'
                : reqs.annualPersonalIncomeWindowYears
                ? ` for the last ${reqs.annualPersonalIncomeWindowYears} years`
                : ''}
            </div>
          )}
          {reqs.maxDebtToIncomeRatio && (
            <div>
              <FundingPartnerMatchIcon
                uniqueId={`${fundingPartner.code}-maxDebtToIncomeRatio`}
                match={match.maxDebtToIncomeRatio}
              />{' '}
              Max debt to income ratio: {reqs.maxDebtToIncomeRatio}%
            </div>
          )}
          {reqs.minMonthlyRevenue && (
            <div>
              <FundingPartnerMatchIcon
                uniqueId={`${fundingPartner.code}-minMonthlyRevenue`}
                match={match.minMonthlyRevenue}
              />{' '}
              Min monthly revenue: {formatMoney(`${reqs.minMonthlyRevenue}`)}
            </div>
          )}
        </div>
      )}

      <div>
        <Button
          type="button"
          variant={!hasMismatches ? 'primary' : 'secondary'}
          className={clsx(
            'funding__action-button',
            !hasMismatches ? 'button-apply' : 'button-colored'
          )}
          onClick={() => {
            if (!url) {
              onIntroductionRequested();
            } else {
              openUrlInNewTab(url, {
                analyticsEvent: AnalyticsEvent.FUNDING_LINK_OPENED,
                properties: { code: fundingPartner.code, source: 'button' },
              });
            }
          }}
          data-test="funding-partner-tile__apply-button"
        >
          {hasMismatches
            ? 'Learn more'
            : typeof fundingPartner.url === 'string' && fundingPartner.url
            ? 'Apply Now'
            : 'Connect me with the lender'}
        </Button>
      </div>
    </div>
  );
};
